import { gql } from 'apollo-boost';
import Breadcrumbs from 'core/components/common/Breadcrumbs';
import CenterLoader from 'core/components/common/CenterLoader';
import { useLinks } from 'core/components/linksContext';
import Select from 'core/components/Select';
import { container, pageHeader } from 'core/style';
import injectSheet, { createStyles } from 'core/style/injectSheet';
import { ITheme, WithStyles } from 'core/style/interfaces';
import React, { useState } from 'react';
import { useGetServiceMarketMapDataQuery } from 'site/graphql';
import useSubscriptions from '../auth/hooks/useSubscriptions';
import InvestmentDisclaimer from '../dividends/InvestmentDisclaimer';
import { HighLowIndex } from '../high-low/const';
import LastUpdate from '../high-low/LastUpdate';
import SocialShare from '../social-share/SocialShare';
import SubscriptionBlock from '../subscription/SubscriptionBlock';
import { securityTypeOptions, Time, timeOptions } from './const';
import TreemapChart from './TreemapChart';

gql`
    query GetServiceMarketMapData {
        highLowData {
            issueCapitalization
            secId
            name
            moscowExchangesIndex
            moscowExchangeBlueChipIndex
            index
            eTF
            d1
            w1
            m1
            y1
            yTD
            currentDate
        }
    }
`;

const ServiceMarketMapPage: React.FC<WithStyles<typeof styles>> = ({ classes }) => {
    const { data, loading } = useGetServiceMarketMapDataQuery({ fetchPolicy: 'network-only' });
    const links = useLinks();
    const [securityType, setSecurityType] = useState<HighLowIndex>(HighLowIndex.Top100);
    const [time, setTime] = useState<Time>(Time.d1);

    const { hasSubscription, hasSubscriptionFreeCourses } = useSubscriptions();
    const hasAnySubscription = hasSubscription || hasSubscriptionFreeCourses;

    if (loading) {
        return <CenterLoader />;
    }

    return (
        <>
            <div className={classes.header}>
                <h1>Карта рынка</h1>
                <h2>
                    Карта рынка акций и ETF Московской биржи. Размер отражает рыночную капитализацию компании
                    относительно капитализации всех выбранных бумаг.
                </h2>
            </div>
            <div className={classes.root}>
                <Breadcrumbs
                    contrast={false}
                    items={[
                        { href: links.home.url(), title: 'Главная' },
                        { href: links.servicePage.url(), title: 'Сервисы инвестора' },
                        { title: 'Карта рынка' },
                    ]}
                />
                <div className={classes.filterContainer}>
                    <LastUpdate lastUpdate={data.highLowData[0]?.currentDate} className={classes.lastUpdate} />
                    <Select
                        options={securityTypeOptions}
                        value={securityType}
                        onChange={value => setSecurityType(value as HighLowIndex)}
                        styles={{ container: { marginRight: 0, width: '100%' } }}
                        redLineOnSelect={false}
                        disabled={!hasAnySubscription}
                    />
                    <Select
                        options={timeOptions}
                        value={time}
                        onChange={value => setTime(value as Time)}
                        styles={{ container: { marginRight: 0, width: '100%' } }}
                        redLineOnSelect={false}
                    />
                </div>
                <SubscriptionBlock label="Для полного доступа необходимо оформить подписку" />
                <TreemapChart securities={data.highLowData} securityType={securityType} time={time} />
                <InvestmentDisclaimer />
                <SocialShare />
            </div>
        </>
    );
};

const styles = (theme: ITheme) =>
    createStyles({
        header: {
            ...pageHeader(theme),
        },
        root: {
            ...container(theme),
        },
        filterContainer: {
            display: 'grid',
            gap: 16 + 'px',

            [theme.breakpoints.up('sm')]: {
                gridTemplate: 'auto auto / 250px 250px auto',
            },
        },
        lastUpdate: {
            [theme.breakpoints.up('sm')]: {
                gridColumn: '1 / span 3',
            },
        },
    });

export default injectSheet(styles)(ServiceMarketMapPage);
